import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { OrLocaleProvider } from '@/lib/locale';
import LoginRoute from '@/routes/LoginRoute';
import CommandCenterRoute from '@/routes/CommandCenterRoute';
import AscShell from '@/components/shell/AscShell';
import CaseWorkspaceRoute from '@/routes/case/CaseWorkspaceRoute';
import SetupMfaRoute from '@/routes/SetupMfaRoute';
import EquipmentListRoute from '@/routes/EquipmentListRoute';
import EquipmentDetailRoute from '@/routes/EquipmentDetailRoute';
import EquipmentCreateRoute from '@/routes/EquipmentCreateRoute';
import SuppliesListRoute from '@/routes/SuppliesListRoute';
import SupplyDetailRoute from '@/routes/SupplyDetailRoute';
import SupplyCreateRoute from '@/routes/SupplyCreateRoute';
import MedicationsListRoute from '@/routes/MedicationsListRoute';
import MedicationDetailRoute from '@/routes/MedicationDetailRoute';
import MedicationCreateRoute from '@/routes/MedicationCreateRoute';
import { DEALogRoute } from '@/routes/dea/DEALogRoute';
import { DEALogDetailRoute } from '@/routes/dea/DEALogDetailRoute';
import { DEAEntryRoute } from '@/routes/dea/DEAEntryRoute';
import { PreferenceCardsRoute } from '@/routes/preference-cards/PreferenceCardsRoute';
import { PreferenceCardDetailRoute } from '@/routes/preference-cards/PreferenceCardDetailRoute';
import { PreferenceCardFormRoute } from '@/routes/preference-cards/PreferenceCardFormRoute';
import { LiveCaseConsoleRoute } from './routes/live-cases/LiveCaseConsoleRoute';
import { CaseDetailRoute } from './routes/live-cases/CaseDetailRoute';
import { FloorPlanRoute } from './routes/floor/FloorPlanRoute';
import { ScheduleRoute } from './routes/ScheduleRoute';
import { StageBoardRoute } from '@/routes/StageBoardRoute';
import { CaseTabIndexRoute } from '@/routes/CaseTabIndexRoute';
import { ReportsRoute } from '@/routes/ReportsRoute';
import { SendConsentRoute } from '@/routes/SendConsentRoute';
import AdminPanelRoute from '@/routes/AdminPanelRoute';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { session, loading, mfaVerified } = useAuth();

    // Still resolving the session, or the server MFA check hasn't returned yet.
    if (loading || (session && mfaVerified === null)) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <span className="pt-spinner" />
                <span className="ml-3 text-pt-text-muted">Loading…</span>
            </div>
        );
    }

    if (!session) return <Navigate to="/login" replace />;

    // MFA is SERVER-ENFORCED via session_has_mfa() (see lib/auth). localStorage
    // is not trusted for the gate; the data layer also fails closed through RLS.
    if (!mfaVerified) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
}

function EnrollmentRoute({ children }: { children: React.ReactNode }) {
    const { session, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <span className="pt-spinner" />
                <span className="ml-3 text-pt-text-muted">Loading…</span>
            </div>
        );
    }

    if (!session) return <Navigate to="/login" replace />;
    return <>{children}</>;
}

// Layout route: gate + persistent ASC shell around every protected app route,
// so the whole application is one surface with the left nav and top bar.
function ProtectedShellLayout() {
    return (
        <ProtectedRoute>
            <AscShell>
                <Outlet />
            </AscShell>
        </ProtectedRoute>
    );
}

function AppRoutes() {
    return (
        <Routes>
            {/* Standalone (outside the shell) */}
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/setup-mfa" element={<EnrollmentRoute><SetupMfaRoute /></EnrollmentRoute>} />

            {/* Everything else lives inside the persistent shell */}
            <Route element={<ProtectedShellLayout />}>
                <Route path="/" element={<CommandCenterRoute />} />
                <Route path="/cases/:caseId" element={<CaseWorkspaceRoute />} />
                <Route path="/schedule" element={<ScheduleRoute />} />

                <Route path="/patient-access" element={<StageBoardRoute kind="access" />} />
                <Route path="/pre-op" element={<StageBoardRoute kind="preop" />} />
                <Route path="/pacu" element={<StageBoardRoute kind="pacu" />} />
                <Route path="/documentation" element={<CaseTabIndexRoute kind="documentation" />} />
                <Route path="/send-consent" element={<SendConsentRoute />} />
                <Route path="/evidence" element={<CaseTabIndexRoute kind="evidence" />} />
                <Route path="/reports" element={<ReportsRoute />} />

                <Route path="/equipment" element={<EquipmentListRoute />} />
                <Route path="/equipment/new" element={<EquipmentCreateRoute />} />
                <Route path="/equipment/:id" element={<EquipmentDetailRoute />} />

                <Route path="/supplies" element={<SuppliesListRoute />} />
                <Route path="/supplies/new" element={<SupplyCreateRoute />} />
                <Route path="/supplies/:id" element={<SupplyDetailRoute />} />

                <Route path="/medications" element={<MedicationsListRoute />} />
                <Route path="/medications/new" element={<MedicationCreateRoute />} />
                <Route path="/medications/:id" element={<MedicationDetailRoute />} />

                <Route path="/dea-log" element={<DEALogRoute />} />
                <Route path="/dea-log/new" element={<DEAEntryRoute />} />
                <Route path="/dea-log/:logId" element={<DEALogDetailRoute />} />

                <Route path="/live-cases" element={<LiveCaseConsoleRoute />} />
                <Route path="/live-cases/:caseId" element={<CaseDetailRoute />} />

                <Route path="/floor" element={<FloorPlanRoute />} />
                <Route path="/admin" element={<AdminPanelRoute />} />

                <Route path="/preference-cards" element={<PreferenceCardsRoute />} />
                <Route path="/preference-cards/new" element={<PreferenceCardFormRoute />} />
                <Route path="/preference-cards/:cardId/edit" element={<PreferenceCardFormRoute />} />
                <Route path="/preference-cards/:cardId" element={<PreferenceCardDetailRoute />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <OrLocaleProvider>
                <AppRoutes />
            </OrLocaleProvider>
        </AuthProvider>
    );
}
